import type { Student } from '../api/students';
import type { PickupTypeFilter, WaitTimeFilter } from '../components/DismissalFilters';
import { getWaitTimeInfo } from './waitTime';

export type SortOption = 'checkedIn' | 'name' | 'grade' | 'waitTime';

export interface FilterOptions {
  searchQuery: string;
  pickupType: PickupTypeFilter;
  waitTime: WaitTimeFilter;
  grade: string;
}

export interface SortOptions {
  sortBy: SortOption;
  sortDirection: 'asc' | 'desc';
}

export const filterStudents = (students: Student[], filters: FilterOptions): Student[] => {
  return students.filter((student) => {
    // Search filter
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      const fullName = `${student.first_name} ${student.last_name}`.toLowerCase();
      const grade = student.grade.toLowerCase();
      if (!fullName.includes(query) && !grade.includes(query)) {
        return false;
      }
    }

    // Pickup type filter
    if (filters.pickupType !== 'all' && student.pickup_type !== filters.pickupType) {
      return false;
    }

    // Wait time filter (only for non-dismissed students)
    if (filters.waitTime !== 'all' && !student.dismissed_at) {
      const waitTimeInfo = getWaitTimeInfo(student);
      if (filters.waitTime === '10min' && waitTimeInfo.minutes < 10) {
        return false;
      }
      if (filters.waitTime === '20min' && waitTimeInfo.minutes < 20) {
        return false;
      }
    }

    // Grade filter
    if (filters.grade && student.grade !== filters.grade) {
      return false;
    }

    return true;
  });
};

export const sortStudents = (students: Student[], options: SortOptions): Student[] => {
  const sorted = [...students];

  sorted.sort((a, b) => {
    let comparison = 0;

    switch (options.sortBy) {
      case 'name': {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        comparison = nameA.localeCompare(nameB);
        break;
      }
      case 'grade': {
        comparison = a.grade.localeCompare(b.grade);
        break;
      }
      case 'checkedIn': {
        const timeA = new Date(a.checked_in_at).getTime();
        const timeB = new Date(b.checked_in_at).getTime();
        comparison = timeB - timeA; // Most recent first by default
        break;
      }
      case 'waitTime': {
        // For dismissed students, use 0 wait time (they'll appear at the bottom)
        const waitA = a.dismissed_at ? 0 : getWaitTimeInfo(a).minutes;
        const waitB = b.dismissed_at ? 0 : getWaitTimeInfo(b).minutes;
        comparison = waitB - waitA; // Longest wait first by default
        break;
      }
    }

    return options.sortDirection === 'asc' ? comparison : -comparison;
  });

  return sorted;
};

export const getUniqueGrades = (students: Student[]): string[] => {
  const grades = new Set(students.map(s => s.grade));
  return Array.from(grades).sort((a, b) => {
    // Try to sort numerically first, then alphabetically
    const numA = parseInt(a, 10);
    const numB = parseInt(b, 10);
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }
    return a.localeCompare(b);
  });
};
